import React, { useEffect, useState } from "react";
import { api, resolveAsset } from "../../lib/api";
import ImageUploader from "../ImageUploader";
import { Plus, X, Save, Loader2 } from "lucide-react";

const EMPTY = { name: "", when: "", rating: 5, text: "", avatar: "", order: 99 };

export default function TestimonialsEditor({ reload }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchItems = async () => {
    const r = await api.get("/testimonials");
    setItems(r.data);
  };
  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const data = { ...editing, rating: Number(editing.rating) || 5, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/testimonials/${editing.id}`, data);
      else await api.post("/testimonials", data);
      setEditing(null);
      await fetchItems();
      reload && reload();
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/testimonials/${id}`);
    await fetchItems();
    reload && reload();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-medium text-gray-800">Testimonials</h2>
        <button onClick={() => setEditing({ ...EMPTY })} className="bg-[#a08862] hover:bg-[#8e7654] text-white text-sm px-4 py-2 rounded flex items-center gap-1">
          <Plus size={14} /> New
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white border rounded p-4 flex gap-3">
            {t.avatar && <img src={resolveAsset(t.avatar)} alt={t.name} className="w-14 h-14 rounded-full object-cover" />}
            <div className="flex-1">
              <div className="flex justify-between">
                <div>
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.when} • {t.rating}★</div>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-1 line-clamp-3">{t.text}</p>
              <div className="flex gap-2 mt-2">
                <button onClick={() => setEditing(t)} className="text-xs px-2 py-1 border rounded">Edit</button>
                <button onClick={() => remove(t.id)} className="text-xs px-2 py-1 border border-red-200 text-red-600 rounded">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-md w-full max-w-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b">
              <h3 className="font-medium">{editing.id ? "Edit" : "New"} Testimonial</h3>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <F label="Name"><I value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} /></F>
                <F label="When"><I value={editing.when} onChange={(v) => setEditing({ ...editing, when: v })} /></F>
                <F label="Rating (1-5)"><I type="number" value={editing.rating} onChange={(v) => setEditing({ ...editing, rating: v })} /></F>
                <F label="Order"><I type="number" value={editing.order} onChange={(v) => setEditing({ ...editing, order: v })} /></F>
              </div>
              <F label="Quote">
                <textarea
                  value={editing.text || ""}
                  onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={4}
                />
              </F>
              <F label="Avatar">
                <ImageUploader value={editing.avatar} onChange={(v) => setEditing({ ...editing, avatar: v })} aspect="square" />
              </F>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border rounded">Cancel</button>
                <button onClick={save} disabled={saving} className="bg-[#a08862] hover:bg-[#8e7654] text-white text-sm px-4 py-2 rounded flex items-center gap-1">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, children }) => (<div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>);
const I = ({ value, onChange, type = "text" }) => (<input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:border-[#a08862]" />);
